import { MEMBER_AVATAR_ALLOWED_CONTENT_TYPES, MEMBER_AVATAR_MAX_SIZE_BYTES } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { boundedPresign, boundedStoragePost } from './bounded-storage-post';

// The chosen file is not a permitted photo (wrong type or too large). Caught
// before any request so the existing photo is retained.
export class PhotoPolicyError extends Error {}

// The presign / direct-to-S3 upload / confirm round-trip failed.
export class PhotoUploadError extends Error {}

export function isAllowedPhoto(file: File): boolean {
  return (
    (MEMBER_AVATAR_ALLOWED_CONTENT_TYPES as ReadonlyArray<string>).includes(file.type) &&
    file.size <= MEMBER_AVATAR_MAX_SIZE_BYTES
  );
}

/**
 * Runs the avatar two-phase upload: presign via the member-avatar endpoint
 * (no org:media:file-write required), POST bytes straight to object storage,
 * then confirm. Returns the confirmed file id to attach to the profile.
 * Throws PhotoPolicyError for a disallowed file and PhotoUploadError for any
 * transport/confirm failure — including a transfer that STALLS, which the
 * stall timeout in {@link boundedStoragePost} converts into that same rejection
 * (BL-113).
 */
export async function uploadMemberPhoto(workspaceId: string, file: File): Promise<string> {
  if (!isAllowedPhoto(file)) {
    throw new PhotoPolicyError();
  }

  const created = await boundedPresign(
    apiClient.memberProfiles.createAvatarUpload({
      pathParams: { workspaceId },
      body: { contentType: file.type, sizeBytes: file.size, originalFilename: file.name },
    }),
    () => new PhotoUploadError(),
  );

  if (!created.ok) {
    throw new PhotoUploadError();
  }

  const { fileId, upload } = created.body;

  await boundedStoragePost(upload, file, () => new PhotoUploadError());

  const confirmed = await apiClient.memberProfiles.confirmAvatarUpload({
    pathParams: { workspaceId, fileId },
    body: {},
  });

  if (!confirmed.ok) {
    throw new PhotoUploadError();
  }

  return fileId;
}

// Resolves a short-lived display URL for a stored photo, or null if it can no
// longer be read (e.g. expired/removed).
export async function resolveMemberPhotoUrl(workspaceId: string, fileId: string): Promise<string | null> {
  const result = await apiClient.memberProfiles.readAvatar({ pathParams: { workspaceId, fileId } });

  return result.ok ? result.body.url : null;
}
